import React, {useState } from "react";
import { useNavigate } from "react-router";
import useApi from "../../../../core/customhook/useApi";
import SearchableSelect from "../../../../core/common/SearchableSelect";
import z from "zod";
import Swal from "sweetalert2";
import { all_routes } from "../../../router/all_routes";
import useProjects from "../../../../core/customhook/useProjects"; 

const required = "This field is required.";

export const programSchema = z.object({ 
  program_code: z.string().trim().min(1, required),
  program_name: z.string().trim().min(1, required),
  description: z.string().trim().min(1, required),
  location_name: z.string().trim().min(1, required),
  is_active: z.string().trim().min(1, required),
});

const AddSingleProgram = () => {
  const navigate = useNavigate();
  const { projects, loading } = useProjects();
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState({
    program_code: "",
    program_name: "",
    description: "",
    location_name: "",
    is_active: "",
  });

  const createProgram = useApi<any>(
    `/api/v1/master/projects/${selectedProjectId}/programs`,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );
 
  const createloading = createProgram.loading;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const isProjectSelected = !!selectedProjectId;

  const handleSubmit = async () => {
    if (!selectedProjectId) {
      setErrors({
        project_id: "Please select Project Code.",
      });
      return;
    }

    const result = programSchema.safeParse(formData);

    if (!result.success) {
      const validationErrors: Record<string, string> = {};

      result.error.issues.forEach((issue) => {
        validationErrors[issue.path[0] as string] = issue.message;
      });

      setErrors(validationErrors);
      return;
    }

    setErrors({});

    try {
      const res = await createProgram.callApi(formData);

      if (res?.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: res.message || "Program created successfully.",
        });
        navigate(all_routes?.program);
      }
    } catch (error: any) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Something went wrong.",
      });
    }
  };

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h5 className="mb-0">Add Program</h5>
        </div>

        <div className="card-body">
          <div className="row">
            {/* Project Code */}
            <div className="col-md-4 mb-3">
              <SearchableSelect
                label="Select Brand Name"
                name="project_id"
                value={selectedProjectId}
                options={projects.map((item: any) => ({
                  label: item.project_name,
                  value: item.project_id,
                }))}
                onChange={(_, value) => {
                  setSelectedProjectId(value);

                  setErrors((prev) => ({
                    ...prev,
                    project_id: "",
                  }));
                }}
                hasRowGrid={false}
                required
              />

              {errors.project_id && (
                <small className="text-danger">{errors.project_id}</small>
              )}
            </div>

            {/* Program Code */}
            <div className="col-md-4 mb-3">
              <label className="form-label">
                Program Code <span className="text-danger">*</span>
              </label>

              <input
                type="text"
                name="program_code"
                className={`form-control ${
                  errors.program_code ? "is-invalid" : ""
                }`}
                placeholder="Enter Program Code"
                value={formData.program_code}
                onChange={handleChange}
                disabled={!isProjectSelected}
              />

              {errors.program_code && (
                <div className="invalid-feedback">{errors.program_code}</div>
              )}
            </div>

            {/* Program Name */}
            <div className="col-md-4 mb-3">
              <label className="form-label">
                Display Name <span className="text-danger">*</span>
              </label>

              <input
                type="text"
                name="program_name"
                className={`form-control ${
                  errors.program_name ? "is-invalid" : ""
                }`}
                placeholder="Enter Display Name"
                value={formData.program_name}
                onChange={handleChange}
                disabled={!isProjectSelected}
              />

              {errors.program_name && (
                <div className="invalid-feedback">{errors.program_name}</div>
              )}
            </div>

            {/* Location */}
            <div className="col-md-4 mb-3">
              <label className="form-label">
                Location <span className="text-danger">*</span>
              </label>

              <input
                type="text"
                name="location_name"
                className={`form-control ${
                  errors.location_name ? "is-invalid" : ""
                }`}
                placeholder="Enter Location"
                value={formData.location_name}
                onChange={handleChange}
                disabled={!isProjectSelected}
              />

              {errors.location_name && (
                <div className="invalid-feedback">{errors.location_name}</div>
              )}
            </div>

            {/* Description */}
            <div className="col-md-4 mb-3">
              <label className="form-label">
                Description <span className="text-danger">*</span>
              </label>

              <input
                type="text"
                name="description"
                className={`form-control ${
                  errors.description ? "is-invalid" : ""
                }`}
                placeholder="Enter Description"
                value={formData.description}
                onChange={handleChange}
                disabled={!isProjectSelected}
              />

              {errors.description && (
                <div className="invalid-feedback">{errors.description}</div>
              )}
            </div>

            {/* Status */}
            <div className="col-md-4 mb-3">
              <label className="form-label">
                Status <span className="text-danger">*</span>
              </label>

              <select
                name="is_active"
                className={`form-select ${
                  errors.is_active ? "is-invalid" : ""
                }`}
                value={formData.is_active}
                onChange={handleChange}
                disabled={!isProjectSelected}
              >
                <option value="">Select Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>

              {errors.is_active && (
                <div className="invalid-feedback">{errors.is_active}</div>
              )}
            </div>
          </div>

          <hr />

          <div className="d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => navigate(-1)}
            >
              Back
            </button>

            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
            >
              {createloading ? "Adding..." : "Add Program"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddSingleProgram;
